import { config } from '../config';

/**
 * ChatWebSocketService - Lean WebSocket connection manager with EventEmitter pattern.
 * 
 * This is the single source of truth for WebSocket connections.
 * Components subscribe to events via on()/off() instead of handler arrays.
 * 
 * Event types emitted:
 *   - 'message'            → individual chat message
 *   - 'group_message'      → group chat message
 *   - 'contact'            → contact created/updated
 *   - 'contacts_list'      → full contacts list response
 *   - 'unread_status'      → unread message status
 *   - 'contact_ai_status'  → AI toggle status change
 *   - 'contact_delete'     → contact deleted
 *   - 'sync_progress'      → sync progress update
 *   - 'human_transfer_notification' → AI→human transfer
 *   - 'status_change'      → connection status changed (connecting/connected/disconnected/error)
 */

export type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'error';
export type WebSocketEventType =
  | 'message'
  | 'group_message'
  | 'contact'
  | 'contacts_list'
  | 'unread_status'
  | 'contact_ai_status'
  | 'contact_delete'
  | 'sync_progress'
  | 'contact_sync_status'
  | 'message_sync_status'
  | 'human_transfer_notification'
  | 'status_change';

type EventHandler = (payload: any) => void;

const GLOBAL_WS_KEY = '__GLOBAL_CHAT_WEBSOCKET_INSTANCE__';

class ChatWebSocketService {
  private socket: WebSocket | null = null;
  private listeners: Map<string, Set<EventHandler>> = new Map();
  private currentSectorId: number | null = null;
  private currentToken: string | null = null;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 10;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private pingInterval: NodeJS.Timeout | null = null;
  private pongTimeout: NodeJS.Timeout | null = null;
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private connectionCheckInterval: NodeJS.Timeout | null = null;
  private lastPongTime: number = 0;
  private lastMessageTime: number = 0;
  private isIntentionalDisconnect: boolean = false;
  private isReconnecting: boolean = false;
  private connectingPromise: Promise<void> | null = null;
  private _isConnected: boolean = false;
  private _connectionStatus: ConnectionStatus = 'disconnected';

  constructor() {
    // Global singleton
    if ((window as any)[GLOBAL_WS_KEY]) {
      return (window as any)[GLOBAL_WS_KEY];
    }
    (window as any)[GLOBAL_WS_KEY] = this;
    this.setupVisibilityListener();
  }

  // ─── EventEmitter API ────────────────────────────────────────────

  /** Subscribe to a WebSocket event type. Returns an unsubscribe function. */
  on(event: string, handler: EventHandler): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(handler);
    return () => this.off(event, handler);
  }

  /** Unsubscribe from a WebSocket event type. */
  off(event: string, handler: EventHandler): void {
    this.listeners.get(event)?.delete(handler);
  }

  /** Emit an event to all subscribers. */
  private emit(event: string, payload: any): void {
    const handlers = this.listeners.get(event);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(payload);
        } catch (error) {
          console.error(`[ChatWS] Error in ${event} handler:`, error);
        }
      });
    }
  }

  // ─── Connection Status ───────────────────────────────────────────

  public get isConnected(): boolean {
    return this._isConnected && this.socket?.readyState === WebSocket.OPEN;
  }

  public get connectionStatus(): ConnectionStatus {
    return this._connectionStatus;
  }

  public isConnectedToSector(sectorId: number): boolean {
    return this.isConnected && this.currentSectorId === sectorId;
  }

  public getConnection(): WebSocket | null {
    return this.socket;
  }

  public getSectorId(): number | null {
    return this.currentSectorId;
  }

  private setConnectionStatus(status: ConnectionStatus) {
    if (this._connectionStatus !== status) {
      console.log(`[ChatWS] Status: ${this._connectionStatus} → ${status}`);
      this._connectionStatus = status;
      this._isConnected = status === 'connected';

      // Emit status change event for subscribers
      this.emit('status_change', { status, sectorId: this.currentSectorId });

      // Global event for components that don't use the hook
      window.dispatchEvent(new CustomEvent('websocketStatusChange', {
        detail: { status, sectorId: this.currentSectorId }
      }));
    }
  }

  // ─── Heartbeat / Ping-Pong ──────────────────────────────────────

  private setupVisibilityListener() {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        if (!this.isConnected && this.currentSectorId && !this.isIntentionalDisconnect) {
          console.log('[ChatWS] Page visible, checking connection...');
          setTimeout(() => this.attemptReconnectIfNeeded(), 2000);
        }
      }
    });
  }

  private startHeartbeat() {
    this.stopHeartbeat();
    this.pingInterval = setInterval(() => this.sendPing(), 30000);
    this.heartbeatInterval = setInterval(() => this.sendHeartbeat(), 60000);
  }

  private stopHeartbeat() {
    if (this.pingInterval) { clearInterval(this.pingInterval); this.pingInterval = null; }
    if (this.heartbeatInterval) { clearInterval(this.heartbeatInterval); this.heartbeatInterval = null; }
    if (this.pongTimeout) { clearTimeout(this.pongTimeout); this.pongTimeout = null; }
  }

  private sendPing() {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({ type: 'ping', timestamp: Date.now() }));
      this.pongTimeout = setTimeout(() => {
        console.error('[ChatWS] Pong timeout - reconnecting...');
        this.handleConnectionError();
      }, 10000);
    }
  }

  private sendPong() {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({ type: 'pong', timestamp: Date.now(), sectorId: this.currentSectorId }));
    }
  }

  private sendHeartbeat() {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({ type: 'heartbeat', timestamp: Date.now() }));
    }
  }

  private startConnectionCheck() {
    this.stopConnectionCheck();
    this.connectionCheckInterval = setInterval(() => {
      if (!this.isConnected && this.currentSectorId && !this.isIntentionalDisconnect && !this.isReconnecting) {
        console.log('[ChatWS] Connection check: attempting reconnect...');
        this.attemptReconnectIfNeeded();
      }
    }, 60000);
  }

  private stopConnectionCheck() {
    if (this.connectionCheckInterval) { clearInterval(this.connectionCheckInterval); this.connectionCheckInterval = null; }
  }

  // ─── Reconnection ───────────────────────────────────────────────

  private async attemptReconnectIfNeeded() {
    if (this.isReconnecting || this.isConnected) return;
    try {
      this.isReconnecting = true;
      const token = this.currentToken || (window as any).sessionStorage?.getItem('@app/token');
      if (token && this.currentSectorId) {
        console.log(`[ChatWS] Reconnecting to sector ${this.currentSectorId}...`);
        await this.connect(token, this.currentSectorId);
      }
    } catch (error) {
      console.error('[ChatWS] Reconnection error:', error);
    } finally {
      this.isReconnecting = false;
    }
  }

  private handleConnectionError() {
    console.log('[ChatWS] Connection error detected');
    this.setConnectionStatus('error');
    this.stopHeartbeat();
    if (!this.isIntentionalDisconnect && this.reconnectAttempts < this.maxReconnectAttempts) {
      this.scheduleReconnect();
    }
  }

  private scheduleReconnect() {
    if (this.reconnectTimeout) clearTimeout(this.reconnectTimeout);

    const baseDelay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
    const jitter = Math.random() * 1000;
    const delay = baseDelay + jitter;

    console.log(`[ChatWS] Reconnect attempt ${this.reconnectAttempts + 1}/${this.maxReconnectAttempts} in ${Math.round(delay)}ms`);
    this.reconnectTimeout = setTimeout(() => {
      this.reconnectAttempts++;
      this.attemptReconnect();
    }, delay);
  }

  private async attemptReconnect() {
    if (this.isIntentionalDisconnect || this.isReconnecting) return;
    try {
      this.isReconnecting = true;
      const token = this.currentToken || (window as any).sessionStorage?.getItem('@app/token');
      if (token && this.currentSectorId) {
        console.log(`[ChatWS] Attempting reconnect to sector ${this.currentSectorId}... (attempt ${this.reconnectAttempts})`);
        await this.connect(token, this.currentSectorId);
      }
    } catch (error) {
      console.error('[ChatWS] Reconnection error:', error);
      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        this.scheduleReconnect();
      } else {
        console.error('[ChatWS] Max reconnect attempts reached');
        this.setConnectionStatus('error');
      }
    } finally {
      this.isReconnecting = false;
    }
  }

  // ─── Connect / Disconnect ───────────────────────────────────────

  async connect(token: string, sectorId: number): Promise<void> {
    // Already connected to same sector
    if (this.isConnected && this.currentSectorId === sectorId && !this.isReconnecting) {
      console.log(`[ChatWS] Already connected to sector ${sectorId}`);
      return;
    }

    // Close existing connection
    if (this.socket && this.socket.readyState !== WebSocket.CLOSED) {
      console.log('[ChatWS] Closing existing connection...');
      this.disconnect();
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    this.isIntentionalDisconnect = false;
    this.reconnectAttempts = 0;
    this.isReconnecting = false;
    this.currentToken = token;
    this.currentSectorId = sectorId;

    this.connectingPromise = new Promise((resolve, reject) => {
      const websocketBaseUrl = process.env.REACT_APP_WEBSOCKET_URL || config.WS_BASE_URL || 'wss://server.mends.pro';
      const wsUrl = `${websocketBaseUrl}/api/v1/ws?sector_id=${sectorId}&token=${encodeURIComponent(token)}`;

      console.log(`[ChatWS] Connecting to WebSocket for sector ${sectorId}...`);
      this.setConnectionStatus('connecting');

      try {
        this.socket = new WebSocket(wsUrl);
      } catch (error) {
        console.error('[ChatWS] Failed to create WebSocket:', error);
        this.setConnectionStatus('error');
        this.scheduleReconnect();
        reject(new Error('Failed to create WebSocket connection'));
        return;
      }

      this.socket.onopen = () => {
        console.log(`[ChatWS] Connected to sector ${sectorId}`);
        this.setConnectionStatus('connected');
        this.reconnectAttempts = 0;
        this.isReconnecting = false;
        this.startHeartbeat();
        this.startConnectionCheck();
        resolve();
      };

      this.socket.onmessage = (event) => {
        try {
          this.lastMessageTime = Date.now();
          const data = JSON.parse(event.data);

          // Handle protocol messages internally
          switch (data.type) {
            case 'ping':
              this.sendPong();
              return;
            case 'pong':
              this.lastPongTime = Date.now();
              if (this.pongTimeout) { clearTimeout(this.pongTimeout); this.pongTimeout = null; }
              return;
            case 'heartbeat_response':
              return;
          }

          // For sync events, normalize the event name
          const eventType = (data.type === 'contact_sync_status' || data.type === 'group_sync_status' || data.type === 'message_sync_status')
            ? (data.type === 'group_sync_status' ? 'group_sync_status' : 'sync_progress')
            : data.type;

          // Emit the event to all subscribers
          this.emit(eventType, data.payload || data);
        } catch (error) {
          console.error('[ChatWS] Error processing message:', error);
        }
      };

      this.socket.onerror = (error) => {
        console.error('[ChatWS] WebSocket error:', error);
        this.setConnectionStatus('error');
      };

      this.socket.onclose = (event) => {
        console.log(`[ChatWS] Connection closed. Code: ${event.code}, Clean: ${event.wasClean}`);
        this.stopHeartbeat();
        this.stopConnectionCheck();

        if (event.code === 401) {
          console.error('[ChatWS] Invalid JWT token');
          this.setConnectionStatus('error');
          return;
        }
        if (event.code === 429) {
          console.error('[ChatWS] Rate limit exceeded');
          this.setConnectionStatus('error');
          return;
        }

        this.setConnectionStatus('disconnected');
        if (!this.isIntentionalDisconnect) {
          this.handleConnectionError();
        }
      };

      // Connection timeout (30s)
      const timeoutId = setTimeout(() => {
        if (this._connectionStatus === 'connecting') {
          console.warn('[ChatWS] Connection timeout');
          this.setConnectionStatus('disconnected');
          if (this.socket) this.socket.close();
        }
      }, 30000);

      const originalOnOpen = this.socket.onopen;
      this.socket.onopen = (event) => {
        clearTimeout(timeoutId);
        if (originalOnOpen && this.socket) {
          originalOnOpen.call(this.socket, event);
        }
      };
    });

    return this.connectingPromise;
  }

  disconnect() {
    console.log('[ChatWS] Disconnecting...');
    this.isIntentionalDisconnect = true;
    this.isReconnecting = false;
    this.stopHeartbeat();
    this.stopConnectionCheck();

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.socket) {
      this.socket.onclose = null;
      this.socket.close();
      this.socket = null;
    }

    this.setConnectionStatus('disconnected');
    this.currentSectorId = null;
    this.currentToken = null;
    this.connectingPromise = null;
    this.reconnectAttempts = 0;
  }

  /** Force a full reconnect cycle. */
  public forceReconnect() {
    if (this.currentSectorId && this.currentToken) {
      console.log('[ChatWS] Force reconnecting...');
      const token = this.currentToken;
      const sectorId = this.currentSectorId;
      this.disconnect();
      setTimeout(() => this.connect(token, sectorId), 2000);
    }
  }

  /** Check if the connection is healthy (recent messages/pongs). */
  public isHealthy(): boolean {
    const now = Date.now();
    const timeSinceLastMessage = now - this.lastMessageTime;
    const timeSinceLastPong = now - this.lastPongTime;
    return this.isConnected &&
      (timeSinceLastMessage < 300000 || this.lastMessageTime === 0) &&
      (timeSinceLastPong < 120000 || this.lastPongTime === 0);
  }

  // ─── Backward Compatibility (deprecated, prefer on/off) ─────────

  /** @deprecated Use on('message', handler) instead */
  addMessageHandler(handler: EventHandler) { this.on('message', handler); }
  /** @deprecated Use off('message', handler) instead */
  removeMessageHandler(handler: EventHandler) { this.off('message', handler); }
  /** @deprecated Use on('group_message', handler) instead */
  addGroupMessageHandler(handler: EventHandler) { this.on('group_message', handler); }
  /** @deprecated Use off('group_message', handler) instead */
  removeGroupMessageHandler(handler: EventHandler) { this.off('group_message', handler); }
  /** @deprecated Use on('contact_ai_status', handler) instead */
  addAIStatusHandler(handler: EventHandler) { this.on('contact_ai_status', handler); }
  /** @deprecated Use off('contact_ai_status', handler) instead */
  removeAIStatusHandler(handler: EventHandler) { this.off('contact_ai_status', handler); }
  /** @deprecated Use on('contact_delete', handler) instead */
  addContactDeleteHandler(handler: EventHandler) { this.on('contact_delete', handler); }
  /** @deprecated Use off('contact_delete', handler) instead */
  removeContactDeleteHandler(handler: EventHandler) { this.off('contact_delete', handler); }
  /** @deprecated Use on('contact', handler) instead */
  addContactEventHandler(handler: EventHandler) { this.on('contact', handler); }
  /** @deprecated Use off('contact', handler) instead */
  removeContactEventHandler(handler: EventHandler) { this.off('contact', handler); }
  /** @deprecated Use on('contacts_list', handler) instead */
  addContactsListHandler(handler: EventHandler) { this.on('contacts_list', handler); }
  /** @deprecated Use off('contacts_list', handler) instead */
  removeContactsListHandler(handler: EventHandler) { this.off('contacts_list', handler); }
  /** @deprecated Use on('sync_progress', handler) instead */
  addSyncProgressHandler(handler: EventHandler) { this.on('sync_progress', handler); }
  /** @deprecated Use off('sync_progress', handler) instead */
  removeSyncProgressHandler(handler: EventHandler) { this.off('sync_progress', handler); }
}

// Export singleton instance
const chatWebSocketService = new ChatWebSocketService();
export default chatWebSocketService;